import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePermissions } from '../contexts/PermissionsContext';
import axios from 'axios';
import { FloppyDisk, Upload, File as FileIcon, Check, CalendarBlank, Printer, FileText, PencilSimple } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { errText } from '../utils/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusOptions = [
  { value: 'active', es: 'Activo', en: 'Active' },
  { value: 'inactive', es: 'Inactivo', en: 'Inactive' },
  { value: 'in_study', es: 'En estudio', en: 'In study' },
  { value: 'excluded', es: 'Excluido', en: 'Excluded' },
  { value: 'withdrawn', es: 'Retirado', en: 'Withdrawn' },
];

function DynamicField({ field, value, onChange, lang, readOnly }) {
  const label = lang === 'es' ? (field.label_es || field.label) : field.label;
  if (field.type === 'text' || field.type === 'email' || field.type === 'phone' || field.type === 'date' || field.type === 'number') {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-stone-700">{label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
        <Input type={field.type === 'phone' ? 'tel' : field.type} value={value || ''} onChange={e => onChange(e.target.value)} className="bg-white border-stone-200" readOnly={readOnly} />
      </div>
    );
  }
  if (field.type === 'select') {
    const opts = field.options || [];
    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-stone-700">{label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger className="bg-white border-stone-200"><SelectValue placeholder="..." /></SelectTrigger>
          <SelectContent>{opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === 'textarea') {
    return (
      <div className="space-y-1.5 md:col-span-2">
        <Label className="text-sm font-medium text-stone-700">{label}</Label>
        <Textarea value={value || ''} onChange={e => onChange(e.target.value)} className="bg-white border-stone-200" rows={3} />
      </div>
    );
  }
  if (field.type === 'boolean') {
    return (
      <div className="flex items-center gap-3 py-2">
        <Switch checked={!!value} onCheckedChange={onChange} />
        <Label className="text-sm text-stone-700">{label}</Label>
      </div>
    );
  }
  return null;
}

function ComputedAge({ birthDate, lang }) {
  if (!birthDate) return null;
  const bd = new Date(birthDate);
  if (isNaN(bd)) return null;
  const today = new Date();
  let age = today.getFullYear() - bd.getFullYear();
  if (today.getMonth() < bd.getMonth() || (today.getMonth() === bd.getMonth() && today.getDate() < bd.getDate())) age--;
  return (
    <div className="mb-4 p-2 bg-stone-50 border border-stone-200 rounded-sm inline-block">
      <span className="text-sm text-stone-600">{lang === 'es' ? 'Edad' : 'Age'}: <span className="font-semibold text-stone-800">{age} {lang === 'es' ? 'anos' : 'years'}</span></span>
    </div>
  );
}

const apptStatusColors = {
  citada: 'bg-blue-50 text-blue-700 border-blue-200',
  anulada: 'bg-stone-50 text-stone-500 border-stone-200',
  no_asiste: 'bg-red-50 text-red-600 border-red-200',
  sala_espera: 'bg-amber-50 text-amber-700 border-amber-200',
  especialista: 'bg-purple-50 text-purple-700 border-purple-200',
  sondas: 'bg-teal-50 text-teal-700 border-teal-200',
  terminada: 'bg-green-50 text-green-700 border-green-200',
};

function AttendanceRatioPanel({ appointments, studies, lang }) {
  // URS-F397: metricas de fiabilidad del voluntario.
  //   asistidas = todo lo que NO sea futura (citada), NO sea anulada y NO sea no_asiste
  //   falladas  = anulada + no_asiste
  //   ratio     = asistidas / (asistidas + falladas) * 100
  const stats = (() => {
    let asistidas = 0;
    let anuladas = 0;
    let noAsiste = 0;
    let citadas = 0;
    for (const a of appointments) {
      if (a.status === 'anulada') anuladas++;
      else if (a.status === 'no_asiste') noAsiste++;
      else if (a.status === 'citada') citadas++;
      else asistidas++; // sala_espera | especialista | sondas | terminada
    }
    const falladas = anuladas + noAsiste;
    const base = asistidas + falladas;
    const ratio = base === 0 ? null : Math.round((asistidas / base) * 100);
    return { asistidas, anuladas, noAsiste, citadas, falladas, base, ratio };
  })();

  const ratioColor = stats.ratio === null
    ? 'bg-stone-100 text-stone-500 border-stone-200'
    : stats.ratio >= 80
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : stats.ratio >= 50
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-red-50 text-red-700 border-red-200';
  const ratioLabel = stats.ratio === null
    ? (lang === 'es' ? 'Sin datos suficientes' : 'Not enough data')
    : `${stats.ratio}%`;

  // Desglose por estudio: cuantas asistidas / falladas por estudio
  const byStudy = {};
  for (const a of appointments) {
    if (a.status === 'citada') continue;
    const key = a.study_id || '__qsalud__';
    if (!byStudy[key]) byStudy[key] = { asistidas: 0, anuladas: 0, noAsiste: 0 };
    if (a.status === 'anulada') byStudy[key].anuladas++;
    else if (a.status === 'no_asiste') byStudy[key].noAsiste++;
    else byStudy[key].asistidas++;
  }
  const perStudyRows = Object.entries(byStudy)
    .map(([sid, s]) => {
      const total = s.asistidas + s.anuladas + s.noAsiste;
      const pct = total === 0 ? null : Math.round((s.asistidas / total) * 100);
      return {
        study_id: sid,
        code: sid === '__qsalud__' ? (lang === 'es' ? 'Q Salud / sin estudio' : 'Q Salud / no study') : (studies[sid]?.code || sid),
        ...s,
        total,
        pct,
      };
    })
    .sort((a, b) => (a.pct || 0) - (b.pct || 0));

  return (
    <div className="space-y-4" data-testid="attendance-ratio-panel">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className={`border rounded-sm p-4 flex flex-col ${ratioColor}`} data-testid="attendance-ratio-kpi">
          <span className="text-xs opacity-70">{lang === 'es' ? 'Ratio de asistencia' : 'Attendance ratio'}</span>
          <span className="text-3xl font-heading font-semibold mt-1">{ratioLabel}</span>
          {stats.base > 0 && (
            <span className="text-[11px] opacity-70 mt-1">
              {stats.asistidas} / {stats.base} {lang === 'es' ? 'citas' : 'appts'}
            </span>
          )}
        </div>
        <div className="border rounded-sm p-4 bg-emerald-50 text-emerald-700 border-emerald-200 flex flex-col">
          <span className="text-xs opacity-70">{lang === 'es' ? 'Asistidas' : 'Attended'}</span>
          <span className="text-3xl font-heading font-semibold mt-1" data-testid="attendance-attended-count">{stats.asistidas}</span>
        </div>
        <div className="border rounded-sm p-4 bg-red-50 text-red-700 border-red-200 flex flex-col">
          <span className="text-xs opacity-70">{lang === 'es' ? 'No asisti\u00f3' : 'No show'}</span>
          <span className="text-3xl font-heading font-semibold mt-1" data-testid="attendance-noshow-count">{stats.noAsiste}</span>
        </div>
        <div className="border rounded-sm p-4 bg-stone-50 text-stone-600 border-stone-200 flex flex-col">
          <span className="text-xs opacity-70">{lang === 'es' ? 'Anuladas' : 'Cancelled'}</span>
          <span className="text-3xl font-heading font-semibold mt-1" data-testid="attendance-cancelled-count">{stats.anuladas}</span>
        </div>
      </div>

      {stats.citadas > 0 && (
        <p className="text-xs text-stone-500 italic">
          {lang === 'es'
            ? `Nota: ${stats.citadas} cita(s) futuras (Citada) no cuentan en el ratio.`
            : `Note: ${stats.citadas} future appointment(s) (Scheduled) are not counted in the ratio.`}
        </p>
      )}

      {/* Desglose por estudio */}
      {perStudyRows.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-stone-800 mb-2">
            {lang === 'es' ? 'Desglose por estudio' : 'Breakdown by study'}
          </h4>
          <div className="border border-stone-200 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{lang === 'es' ? 'Estudio' : 'Study'}</TableHead>
                  <TableHead className="text-center">{lang === 'es' ? 'Asistidas' : 'Attended'}</TableHead>
                  <TableHead className="text-center">{lang === 'es' ? 'No asisti\u00f3' : 'No show'}</TableHead>
                  <TableHead className="text-center">{lang === 'es' ? 'Anuladas' : 'Cancelled'}</TableHead>
                  <TableHead className="text-center">{lang === 'es' ? 'Ratio' : 'Ratio'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {perStudyRows.map(r => {
                  const cls = r.pct === null
                    ? 'bg-stone-50 text-stone-500 border-stone-200'
                    : r.pct >= 80
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : r.pct >= 50
                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'bg-red-50 text-red-700 border-red-200';
                  return (
                    <TableRow key={r.study_id} data-testid={`attendance-study-row-${r.study_id}`}>
                      <TableCell className="text-sm font-medium">{r.code}</TableCell>
                      <TableCell className="text-sm text-center">{r.asistidas}</TableCell>
                      <TableCell className="text-sm text-center">{r.noAsiste}</TableCell>
                      <TableCell className="text-sm text-center">{r.anuladas}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={`text-xs ${cls}`}>
                          {r.pct === null ? '—' : `${r.pct}%`}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}

function AppointmentsPanel({ volunteerId, volunteerName, lang }) {
  const [appointments, setAppointments] = useState([]);
  const [studies, setStudies] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCancelled, setShowCancelled] = useState(false);
  const [subTab, setSubTab] = useState('list');

  const lb = lang === 'es'
    ? { title: 'Citas del voluntario', no_appts: 'Este voluntario no tiene citas programadas', date: 'Fecha', time: 'Hora', study: 'Estudio', visit: 'Visita', status: 'Estado', citada: 'Citada', anulada: 'Anulada', no_asiste: 'No asiste', sala_espera: 'Sala de espera', especialista: 'Especialista', sondas: 'Sondas', terminada: 'Terminada', print: 'Imprimir citas', show_cancelled: 'Mostrar anuladas', cancelled_count: 'anuladas' }
    : { title: 'Volunteer appointments', no_appts: 'This volunteer has no scheduled appointments', date: 'Date', time: 'Time', study: 'Study', visit: 'Visit', status: 'Status', citada: 'Scheduled', anulada: 'Cancelled', no_asiste: 'No show', sala_espera: 'Waiting room', especialista: 'Specialist', sondas: 'Probes', terminada: 'Completed', print: 'Print appointments', show_cancelled: 'Show cancelled', cancelled_count: 'cancelled' };

  useEffect(() => {
    if (!volunteerId) { setLoading(false); return; }
    (async () => {
      try {
        const [ar, sr] = await Promise.all([
          axios.get(`${API_URL}/api/appointments?volunteer_id=${volunteerId}&include_cancelled=true`, { withCredentials: true }),
          axios.get(`${API_URL}/api/studies`, { withCredentials: true }),
        ]);
        setAppointments(ar.data || []);
        const m = {};
        (sr.data || []).forEach(s => { m[s.study_id] = s; });
        setStudies(m);
      } catch (err) { if (err?.response?.status !== 401) console.error('Error loading volunteer data:', err); }
      setLoading(false);
    })();
  }, [volunteerId]);

  const scheduledCount = appointments.filter(a => a.status === 'citada').length;
  const cancelledCount = appointments.filter(a => a.status === 'anulada').length;
  const visibleAppointments = showCancelled ? appointments : appointments.filter(a => a.status !== 'anulada');

  const handlePrint = () => {
    const scheduled = appointments.filter(a => a.status === 'citada');
    if (scheduled.length === 0) return;
    const logoUrl = window.location.origin + '/logo-goya.png';
    // URS-F491 (privacidad/ciego): NO imprimir nombre del voluntario (se
    // sustituye por el volunteer_id). El subtipo del estudio (phase) SI
    // se imprime.
    const rows = scheduled.map(a => `<tr><td>${a.date}</td><td>${a.time || '-'}</td><td>${studies[a.study_id]?.phase || ''}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><title>Citas</title><style>
      @page { size: 80mm auto; margin: 0 1mm 1mm 1mm; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Courier New', monospace; width: 76mm; padding: 0 2mm 1.5mm 2mm; color: #000; font-size: 9px; line-height: 1.25; }
      .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 1.5mm; margin-bottom: 1.5mm; }
      .header img { width: 50mm; height: auto; margin-bottom: 0.5mm; filter: grayscale(100%) contrast(150%); }
      .header .phone { font-size: 9px; margin-top: 0.8mm; }
      .header .ev-clinicas { font-size: 8px; margin-top: 0.8mm; }
      .header .name { font-size: 9px; font-weight: bold; margin-top: 1.5mm; }
      .header .subtitle { font-size: 8px; margin-top: 0.8mm; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 1.5mm; }
      th { font-size: 8px; text-align: left; border-bottom: 1px solid #000; padding: 0.8mm 0; font-weight: bold; }
      td { font-size: 9px; padding: 1.2mm 0; border-bottom: 1px dotted #ccc; }
    </style></head><body>
      <div class="header">
        <img src="${logoUrl}" alt="Goya Innova" />
        <div class="phone">Tel: 911 928 904</div>
        <div class="ev-clinicas">Evaluaciones Cl\u00ednicas</div>
        <div class="name">${volunteerId || ''}</div>
        <div class="subtitle">${lang === 'es' ? 'Citas' : 'Appointments'}</div>
      </div>
      <table><thead><tr><th>${lb.date}</th><th>${lb.time}</th><th></th></tr></thead><tbody>${rows}</tbody></table>
      <script>window.onload=function(){window.print();}<\/script>
    </body></html>`;
    const w = window.open('', '_blank');
    w.document.open();
    w.document.writeln(html);
    w.document.close();
  };

  if (!volunteerId) return <p className="text-sm text-stone-500 text-center py-8">{lang === 'es' ? 'Guarda el voluntario primero' : 'Save the volunteer first'}</p>;
  if (loading) return <div className="p-12 text-center text-sm text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</div>;
  if (appointments.length === 0) return (
    <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
      <CalendarBlank size={40} className="text-stone-300 mx-auto mb-3" />
      <p className="text-sm text-stone-500">{lb.no_appts}</p>
    </div>
  );

  return (
    <div className="space-y-4">
      <Tabs value={subTab} onValueChange={setSubTab} className="w-full">
        <TabsList className="bg-stone-100">
          <TabsTrigger value="list" data-testid="vol-appts-subtab-list">
            {lang === 'es' ? 'Listado' : 'List'}
          </TabsTrigger>
          <TabsTrigger value="ratio" data-testid="vol-appts-subtab-ratio">
            {lang === 'es' ? 'Ratio de asistencia' : 'Attendance ratio'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <h3 className="text-base font-semibold text-stone-800">{lb.title}</h3>
              {cancelledCount > 0 && (
                <button
                  type="button"
                  data-testid="vol-appts-toggle-cancelled"
                  onClick={() => setShowCancelled(p => !p)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-xs font-medium transition-colors ${showCancelled ? 'bg-stone-700 text-white border-stone-700' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${showCancelled ? 'bg-white' : 'bg-stone-300'}`} />
                  {lb.show_cancelled} ({cancelledCount})
                </button>
              )}
            </div>
            <Button variant="outline" onClick={handlePrint} disabled={scheduledCount === 0} className="rounded-sm gap-2 text-sm">
              <Printer size={14} />{lb.print} ({scheduledCount})
            </Button>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>{lb.date}</TableHead><TableHead>{lb.time}</TableHead>
              <TableHead>{lb.study}</TableHead><TableHead>{lb.visit}</TableHead><TableHead>{lb.status}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {visibleAppointments.map(a => (
                <TableRow key={a.appointment_id} className={a.status === 'anulada' ? 'opacity-60' : ''}>
                  <TableCell className="text-sm">{a.date}</TableCell>
                  <TableCell className="text-sm">{a.time || '-'}</TableCell>
                  <TableCell className="text-sm">{studies[a.study_id]?.code || a.study_id}</TableCell>
                  <TableCell className="text-sm">{a.visit_code}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-xs ${apptStatusColors[a.status] || ''}`}>{lb[a.status] || a.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="ratio" className="mt-4">
          <AttendanceRatioPanel appointments={appointments} studies={studies} lang={lang} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ConsentDocSection({ template, docData, volunteerId, onSigned, onUpload, uploading, lang }) {
  const doc = docData || {};
  const label = lang === 'es' ? (template.name_es || template.name) : template.name;
  return (
    <div className="bg-white border border-stone-200 rounded-sm p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText size={20} className={doc.signed ? 'text-green-500' : 'text-stone-300'} />
          <div>
            <p className="text-sm font-medium text-stone-800">{label}</p>
            {doc.file_name && <p className="text-xs text-stone-400">{doc.file_name}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {doc.signed && <Badge className="bg-green-50 text-green-700 border-green-200 text-xs"><Check size={12} className="mr-1" />{lang === 'es' ? 'Firmado' : 'Signed'}</Badge>}
          <label>
            <input type="file" className="hidden" onChange={e => e.target.files?.[0] && onUpload(template.type, e.target.files[0])} />
            <Button variant="outline" size="sm" asChild className="cursor-pointer rounded-sm gap-1">
              <span><Upload size={12} />{uploading === template.type ? '...' : (lang === 'es' ? 'Subir' : 'Upload')}</span>
            </Button>
          </label>
        </div>
      </div>
    </div>
  );
}

export default function VolunteerModal({ volunteerId, open, onClose }) {
  const { lang } = useLanguage();
  const { can } = usePermissions();
  // URS-F348: pestana Q Salud gateada por permiso transversal `qsalud`.
  const canSeeQsalud = can('qsalud', 'view');

  const [fields, setFields] = useState([]);
  const [personalData, setPersonalData] = useState({});
  const [healthData, setHealthData] = useState({});
  const [status, setStatus] = useState('active');
  // URS-F429: timestamp de ultima actualizacion del Q Salud
  const [qsaludLastReviewedAt, setQsaludLastReviewedAt] = useState(null);
  const [documents, setDocuments] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [consentTemplates, setConsentTemplates] = useState([]);

  useEffect(() => {
    if (!volunteerId || !open) return;
    setLoading(true);
    (async () => {
      try {
        const [fc, ct, vol] = await Promise.all([
          axios.get(`${API_URL}/api/config/volunteer-fields`, { withCredentials: true }),
          axios.get(`${API_URL}/api/consent-templates`, { withCredentials: true }),
          axios.get(`${API_URL}/api/volunteers/${volunteerId}`, { withCredentials: true }),
        ]);
        setFields(fc.data || []);
        // Dedupe consent templates by `type` (defense in depth — same as VolunteerForm).
        const ctList = ct.data || [];
        const byType = new Map();
        ctList.forEach((t) => {
          const key = t.type || `__${t.template_id}`;
          const ts = t.updated_at || t.created_at || '';
          const prev = byType.get(key);
          if (!prev || (prev.updated_at || prev.created_at || '') < ts) byType.set(key, t);
        });
        setConsentTemplates(Array.from(byType.values()));
        setPersonalData(vol.data.personal_data || {});
        setHealthData(vol.data.health_questionnaire || {});
        setStatus(vol.data.status || 'active');
        setQsaludLastReviewedAt(vol.data.qsalud_last_reviewed_at || null);
        if (vol.data.documents) setDocuments(vol.data.documents);
      } catch { toast.error('Error loading volunteer'); }
      setLoading(false);
    })();
  }, [volunteerId, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/volunteers/${volunteerId}`, {
        status, personal_data: personalData, health_questionnaire: healthData, documents,
      }, { withCredentials: true });
      toast.success(lang === 'es' ? 'Voluntario actualizado' : 'Volunteer updated');
      // URS-F429: refrescar timestamp de Q Salud actualizado por el backend
      try {
        const { data: vol } = await axios.get(`${API_URL}/api/volunteers/${volunteerId}`, { withCredentials: true });
        setQsaludLastReviewedAt(vol.qsalud_last_reviewed_at || null);
      } catch { /* silent */ }
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  const handleConsentUpload = async (docKey, file) => {
    setUploading(docKey);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/files/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setDocuments(prev => ({ ...prev, [docKey]: { file_id: data.id, file_name: data.original_filename, signed: true, uploaded_at: data.created_at, signed_at: data.created_at } }));
      toast.success(lang === 'es' ? 'Documento subido' : 'Document uploaded');
    } catch { toast.error(lang === 'es' ? 'Error al subir' : 'Upload failed'); }
    setUploading(null);
  };

  const personalFields = fields.filter(f => f.tab === 'personal_data' && f.active).sort((a, b) => a.order - b.order);
  const healthFields = fields.filter(f => f.tab === 'health_questionnaire' && f.active).sort((a, b) => a.order - b.order);
  const consentDocKeys = consentTemplates.map(t => t.type);
  const signedCount = consentDocKeys.filter(k => documents[k]?.signed).length;
  const totalDocs = consentDocKeys.length || 1;
  const docStatus = consentDocKeys.length === 0 ? 'red' : signedCount === totalDocs ? 'green' : signedCount > 0 ? 'yellow' : 'red';
  const docColors = { red: 'bg-red-500', yellow: 'bg-amber-400', green: 'bg-green-500' };
  const docLabels = { es: { red: 'Sin documentos', yellow: 'Parcial', green: 'Completo' }, en: { red: 'No documents', yellow: 'Partial', green: 'Complete' } };
  const volName = `${personalData.nombre || ''} ${personalData.apellidos || ''}`.trim();

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="volunteer-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-stone-900">
            {volName || volunteerId}
            <span className="text-xs font-mono text-stone-400 ml-2">{volunteerId}</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="p-12 text-center text-sm text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Label className="text-sm font-medium text-stone-800">{lang === 'es' ? 'Estado' : 'Status'}:</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="w-36 bg-white border-stone-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {statusOptions.map(s => <SelectItem key={s.value} value={s.value}>{lang === 'es' ? s.es : s.en}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2" size="sm">
                <FloppyDisk size={14} />{saving ? '...' : (lang === 'es' ? 'Guardar' : 'Save')}
              </Button>
            </div>

            <Tabs defaultValue="personal" className="w-full">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <TabsList className="bg-stone-100">
                  <TabsTrigger value="personal">{lang === 'es' ? 'Datos Personales' : 'Personal Data'}</TabsTrigger>
                  {canSeeQsalud && <TabsTrigger value="health">{lang === 'es' ? 'Q Salud' : 'Health Q'}</TabsTrigger>}
                  <TabsTrigger value="documents">{lang === 'es' ? 'Documentos' : 'Documents'}</TabsTrigger>
                  <TabsTrigger value="appointments">{lang === 'es' ? 'Citas' : 'Appointments'}</TabsTrigger>
                </TabsList>
                {canSeeQsalud && qsaludLastReviewedAt && (
                  <span
                    data-testid="qsalud-last-reviewed-label"
                    className="text-xs text-stone-600 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-sm whitespace-nowrap"
                    title={new Date(qsaludLastReviewedAt).toLocaleString()}
                  >
                    {lang === 'es' ? 'Q Salud actualizado a ' : 'Q Salud updated at '}
                    <strong className="text-emerald-800">{new Date(qsaludLastReviewedAt).toLocaleDateString()}</strong>
                  </span>
                )}
              </div>

              <TabsContent value="personal" className="mt-4">
                <div className="bg-white border border-stone-200 rounded-sm p-4">
                  <div className="flex items-center gap-3 mb-4 p-2 border border-stone-200 rounded-sm bg-stone-50">
                    <Label className="text-sm font-medium text-stone-800">{lang === 'es' ? 'Documentacion' : 'Documentation'}:</Label>
                    <span className={`inline-block w-3 h-3 rounded-full ${docColors[docStatus]}`} />
                    <span className={`text-sm ${docStatus === 'red' ? 'text-red-600' : docStatus === 'yellow' ? 'text-amber-600' : 'text-green-600'} font-medium`}>
                      {docLabels[lang]?.[docStatus]}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {personalFields.map(f => (
                      <DynamicField key={f.field_key} field={f} value={personalData[f.field_key]} onChange={val => setPersonalData(prev => ({ ...prev, [f.field_key]: val }))} lang={lang} readOnly={f.field_key === 'num_voluntario'} />
                    ))}
                  </div>
                </div>
              </TabsContent>

              {canSeeQsalud && <TabsContent value="health" className="mt-4">
                <div className="bg-white border border-stone-200 rounded-sm p-4">
                  <ComputedAge birthDate={personalData.fecha_nacimiento} lang={lang} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {healthFields.map(f => (
                      <DynamicField key={f.field_key} field={f} value={healthData[f.field_key]} onChange={val => setHealthData(prev => ({ ...prev, [f.field_key]: val }))} lang={lang} />
                    ))}
                  </div>
                </div>
              </TabsContent>}

              <TabsContent value="documents" className="mt-4">
                <div className="space-y-3">
                  {consentTemplates.length > 0 ? consentTemplates.map(tpl => (
                    <ConsentDocSection key={tpl.template_id} template={tpl} docData={documents[tpl.type]} volunteerId={volunteerId} onSigned={(k, d) => setDocuments(prev => ({ ...prev, [k]: d }))} onUpload={handleConsentUpload} uploading={uploading} lang={lang} />
                  )) : (
                    <div className="bg-white border border-stone-200 rounded-sm p-8 text-center">
                      <FileText size={32} className="mx-auto text-stone-300 mb-2" />
                      <p className="text-sm text-stone-500">{lang === 'es' ? 'No hay plantillas de consentimiento configuradas' : 'No consent templates configured'}</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="appointments" className="mt-4">
                <AppointmentsPanel volunteerId={volunteerId} volunteerName={volName} lang={lang} />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
